import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Target, AlertCircle, Eye, EyeOff, CheckCircle } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (password !== confirm) { setError("Las contraseñas no coinciden."); return; }
    setLoading(true);
    try {
      await api.post("/auth/register", { email: email.trim().toLowerCase(), password });
      await login(email.trim().toLowerCase(), password);
      navigate("/onboarding");
    } catch (err) {
      setError(err?.response?.data?.detail || "Error al crear la cuenta. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] flex flex-col items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl mb-4">
              <Target className="w-7 h-7 text-emerald-400" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-slate-50 tracking-tight">RadarLit</h1>
          </Link>
          <p className="text-emerald-400 text-sm mt-1 font-medium">Inteligencia para contratar con el Estado</p>
          <div className="mt-3 inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-emerald-400 text-xs font-medium">7 días gratis · Sin tarjeta</span>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-slate-200 font-semibold text-lg mb-1">Crear cuenta</h2>
          <p className="text-slate-400 text-sm mb-6">Empieza tu prueba gratuita de 7 días.</p>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg px-4 py-3 mb-5">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-slate-400 text-xs font-medium mb-1.5 block">Correo electrónico</label>
              <input
                data-testid="register-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="email"
                className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                placeholder="tu@empresa.com"
              />
            </div>
            <div>
              <label className="text-slate-400 text-xs font-medium mb-1.5 block">Contraseña</label>
              <div className="relative">
                <input
                  data-testid="register-password"
                  type={showPwd ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required minLength={8}
                  className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 pr-10 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                  placeholder="Mínimo 8 caracteres"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-slate-400 text-xs font-medium mb-1.5 block">Confirmar contraseña</label>
              <input
                data-testid="register-confirm"
                type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                placeholder="Repite la contraseña"
              />
            </div>
            <button
              data-testid="register-submit"
              type="submit" disabled={loading}
              className="w-full btn-primary rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50 mt-2"
            >
              {loading ? "Creando cuenta..." : "Comenzar prueba gratuita"}
            </button>
          </form>

          <p className="text-center text-slate-500 text-xs mt-5">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-emerald-400 hover:underline">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
